/**
 * Lexical 自定义节点类型定义
 *
 * 定义所有投资相关的 Lexical 节点类型接口
 */

import type { SerializedLexicalNode } from "lexical";

// =============================================================================
// 决策记录节点 (DecisionNode)
// =============================================================================

export interface SerializedDecisionNode extends SerializedLexicalNode {
  type: "decision";
  version: number;
  // === Core Fields (Required) ===
  ticker: string; // Stock symbol (e.g., "NVDA")
  decision: "BUY" | "SELL" | "HOLD";

  // === Scenario Fields (Optional) ===
  contractId?: string; // Reference to SelfContract (only when decision has associated contract)
  actionType: "FOLLOW" | "OVERRIDE" | "NONE";

  // === Execution Data (Required) ===
  price: number; // Execution price
  quantity: number; // Number of shares
  reason: string; // Decision rationale
  executedAt: string; // ISO 8601 timestamp

  // === Pre-Analysis Data (Optional) ===
  confidence?: number; // 0-100 confidence score
  riskLevel?: "LOW" | "MEDIUM" | "HIGH";
  timeHorizon?: string; // Time horizon for the decision

  // === Metadata ===
  createdAt?: string; // ISO 8601 timestamp
}

export type DecisionData = {
  ticker: string;
  contractId?: string;
  decision: "BUY" | "SELL" | "HOLD";
  actionType: "FOLLOW" | "OVERRIDE" | "NONE";
  price: number;
  quantity: number;
  reason: string;
  executedAt: string;
  createdAt?: string;
  confidence?: number;
  riskLevel?: "LOW" | "MEDIUM" | "HIGH";
  timeHorizon?: string;
};

// =============================================================================
// Checklist 节点 (ChecklistNode)
// =============================================================================

export interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
  category?: string;
}

export interface SerializedChecklistNode extends SerializedLexicalNode {
  type: "checklist";
  version: 1;
  items: ChecklistItem[];
  title?: string;
}

export type ChecklistData = {
  items: ChecklistItem[];
  title?: string;
};

// =============================================================================
// 股价走势节点 (PriceTrendNode)
// =============================================================================

export interface SerializedPriceTrendNode extends SerializedLexicalNode {
  type: "price-trend";
  version: 1;
  symbol: string; // 股票代码
  trend: "BULLISH" | "BEARISH" | "NEUTRAL";
  support: number; // 支撑位
  resistance: number; // 阻力位
  target: number; // 目标价
  stopLoss?: number; // 止损价
  notes?: string;
  // 可选：图表数据（如 K线截图URL）
  chartUrl?: string;
}

export type PriceTrendData = {
  symbol: string;
  trend: "BULLISH" | "BEARISH" | "NEUTRAL";
  support: number;
  resistance: number;
  target: number;
  stopLoss?: number;
  notes?: string;
  chartUrl?: string;
};

// =============================================================================
// 行动项节点 (ActionItemNode)
// =============================================================================

export interface SerializedActionItemNode extends SerializedLexicalNode {
  type: "action-item";
  version: 1;
  action: string; // 行动描述
  priority: "high" | "medium" | "low";
  status: "todo" | "in-progress" | "done";
  dueDate?: string; // ISO 8601 日期字符串
  assignee?: string; // 负责人
  notes?: string;
}

export type ActionItemData = {
  action: string;
  priority: "high" | "medium" | "low";
  status: "todo" | "in-progress" | "done";
  dueDate?: string;
  assignee?: string;
  notes?: string;
};

// =============================================================================
// 自定约节点 (SelfContractNode)
// =============================================================================

export interface SerializedSelfContractNode extends SerializedLexicalNode {
  type: "self-contract";
  version: 1;
  ticker: string; // Stock symbol (e.g., "NVDA")
  template: "value-oriented" | "trading-oriented";
  expiration: string; // ISO 8601 date (YYYY-MM-DD)
  idea: string; // Investment idea/thesis
  trigger: {
    type: "price" | "expiration" | "time" | "custom";
    priceLevel?: number;
    operator?: ">=" | "<=" | "==" | ">" | "<";
    notes: string;
  };
  action: string; // Action to take on trigger
  status: "active" | "triggered" | "cancelled" | "expired";
  createdAt?: string; // ISO 8601 timestamp
  updatedAt?: string; // ISO 8601 timestamp
}

export type SelfContractData = {
  ticker: string;
  template: "value-oriented" | "trading-oriented";
  expiration: string;
  idea: string;
  trigger: {
    type: "price" | "expiration" | "time" | "custom";
    priceLevel?: number;
    operator?: ">=" | "<=" | "==" | ">" | "<";
    notes: string;
  };
  action: string;
  status: "active" | "triggered" | "cancelled" | "expired";
  createdAt?: string;
  updatedAt?: string;
};

// =============================================================================
// 风险评估节点 (RiskAssessmentNode) - 未来扩展
// =============================================================================

export interface RiskFactor {
  category: "MARKET" | "COMPANY" | "SECTOR" | "MACRO";
  level: "LOW" | "MEDIUM" | "HIGH";
  description: string;
  mitigation?: string; // 缓解措施
}

export interface SerializedRiskAssessmentNode extends SerializedLexicalNode {
  type: "risk-assessment";
  version: 1;
  overallRisk: "LOW" | "MEDIUM" | "HIGH";
  factors: RiskFactor[];
  notes?: string;
}

export type RiskAssessmentData = {
  overallRisk: "LOW" | "MEDIUM" | "HIGH";
  factors: RiskFactor[];
  notes?: string;
};

// =============================================================================
// 仓位管理节点 (PositionSizeNode) - 未来扩展
// =============================================================================

export interface SerializedPositionSizeNode extends SerializedLexicalNode {
  type: "position-size";
  version: 1;
  symbol: string;
  entryPrice: number;
  targetPrice: number;
  positionSize: number; // 仓位大小（股数或金额）
  positionPercent: number; // 占总投资组合百分比
  stopLoss?: number;
  notes?: string;
}

export type PositionSizeData = {
  symbol: string;
  entryPrice: number;
  targetPrice: number;
  positionSize: number;
  positionPercent: number;
  stopLoss?: number;
  notes?: string;
};

// =============================================================================
// 退出策略节点 (ExitStrategyNode) - 未来扩展
// =============================================================================

export interface ExitCondition {
  type: "TARGET_PRICE" | "STOP_LOSS" | "TIME_BASED" | "CONDITION_BASED";
  description: string;
  triggered: boolean;
}

export interface SerializedExitStrategyNode extends SerializedLexicalNode {
  type: "exit-strategy";
  version: 1;
  symbol: string;
  conditions: ExitCondition[];
  trailingStopLoss?: number;
  notes?: string;
}

export type ExitStrategyData = {
  symbol: string;
  conditions: ExitCondition[];
  trailingStopLoss?: number;
  notes?: string;
};

// =============================================================================
// 辅助类型
// =============================================================================

/**
 * 所有自定义节点类型的联合类型
 */
export type CustomNodeTypes =
  | SerializedDecisionNode
  | SerializedChecklistNode
  | SerializedPriceTrendNode
  | SerializedActionItemNode
  | SerializedRiskAssessmentNode
  | SerializedPositionSizeNode
  | SerializedExitStrategyNode
  | SerializedSelfContractNode;

/**
 * 节点类型字符串字面量
 */
export type CustomNodeType = CustomNodeTypes["type"];

/**
 * 投资决策的中文显示映射
 */
export const DECISION_LABELS: Record<string, string> = {
  BUY: "买入",
  SELL: "卖出",
  HOLD: "持有",
};

/**
 * 风险等级的中文显示映射
 */
export const RISK_LEVEL_LABELS: Record<string, string> = {
  LOW: "低风险",
  MEDIUM: "中等风险",
  HIGH: "高风险",
};

/**
 * 趋势的中文显示映射
 */
export const TREND_LABELS: Record<string, string> = {
  BULLISH: "看涨",
  BEARISH: "看跌",
  NEUTRAL: "中性",
};

/**
 * 优先级的中文显示映射
 */
export const PRIORITY_LABELS: Record<string, string> = {
  high: "高",
  medium: "中",
  low: "低",
};

/**
 * 状态的中文显示映射
 */
export const STATUS_LABELS: Record<string, string> = {
  todo: "待办",
  "in-progress": "进行中",
  done: "已完成",
};

/**
 * 自定约状态的中文显示映射
 */
export const CONTRACT_STATUS_LABELS: Record<string, string> = {
  active: "进行中",
  triggered: "已触发",
  cancelled: "已取消",
  expired: "已过期",
};

/**
 * 自定约模板的中文显示映射
 */
export const CONTRACT_TEMPLATE_LABELS: Record<string, string> = {
  "value-oriented": "价值导向",
  "trading-oriented": "波段导向",
};
